import { Injectable, Optional } from '@angular/core';
import { blockingLoaderHeaders } from 'app/core/interceptors/loader.interceptor';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { catchError, map, Observable, of, throwError } from 'rxjs';
import { ApiResponse } from '@interfaces/httpResponses';
import { Payment } from '@interfaces/Payments';
import { environment } from 'environments/environment';
import { createUuid } from '@utils/uuid';

interface StoredPaymentCommand {
  commandId: string;
  fingerprint: string;
  pending: boolean;
}

export interface PaymentCommandLease {
  commandId: string;
  shouldSubmit: boolean;
}

const STORAGE_PREFIX = 'gd_payment_command:';

const stableSerialize = (value: unknown): string => {
  if (value instanceof Date) return JSON.stringify(value.toISOString());
  if (Array.isArray(value)) {
    return `[${value.map((item) => stableSerialize(item)).join(',')}]`;
  }
  if (value && typeof value === 'object') {
    const entries = Object.entries(value as Record<string, unknown>)
      .filter(([, item]) => item !== undefined)
      .sort(([left], [right]) => left.localeCompare(right));
    return `{${entries
      .map(([key, item]) => `${JSON.stringify(key)}:${stableSerialize(item)}`)
      .join(',')}}`;
  }
  return JSON.stringify(value) ?? 'undefined';
};

export const clearStoredPaymentCommands = (): void => {
  for (let index = sessionStorage.length - 1; index >= 0; index -= 1) {
    const key = sessionStorage.key(index);
    if (key?.startsWith(STORAGE_PREFIX)) sessionStorage.removeItem(key);
  }
};

@Injectable({ providedIn: 'root' })
export class PaymentCommandStoreService {
  private readonly pendingScopes = new Set<string>();

  constructor(@Optional() private readonly httpClient?: HttpClient) {}

  public recover(commandId: string): Observable<Payment | null> {
    if (!this.httpClient) {
      return throwError(() => new Error('HttpClient is required for recovery'));
    }
    return this.httpClient
      .get<ApiResponse<Payment>>(
        `${environment.apiUrl}user/payments/commands/${commandId}`,
        {
          headers: blockingLoaderHeaders({
            message: 'Verificando el cobro...',
            money: true,
          }),
        },
      )
      .pipe(
        map((response) => response.payload),
        catchError((error: HttpErrorResponse) => {
          if (error.status === 404) return of(null);
          return throwError(() => error);
        }),
      );
  }

  public unresolved(
    scope: string,
  ): { commandId: string; command: unknown } | null {
    const stored = this.read(this.getStorageKey(scope));
    if (!stored) return null;
    try {
      return {
        commandId: stored.commandId,
        command: JSON.parse(stored.fingerprint) as unknown,
      };
    } catch {
      return null;
    }
  }

  public acquire(scope: string, semanticCommand: unknown): PaymentCommandLease {
    const storageKey = this.getStorageKey(scope);
    const fingerprint = stableSerialize(semanticCommand);
    const existing = this.read(storageKey);

    if (existing?.fingerprint === fingerprint) {
      const shouldSubmit = !this.pendingScopes.has(storageKey);
      if (shouldSubmit) this.pendingScopes.add(storageKey);
      this.write(storageKey, { ...existing, pending: true });
      return { commandId: existing.commandId, shouldSubmit };
    }

    const commandId = createUuid();
    this.pendingScopes.add(storageKey);
    this.write(storageKey, { commandId, fingerprint, pending: true });
    return { commandId, shouldSubmit: true };
  }

  public markAmbiguous(scope: string): void {
    const storageKey = this.getStorageKey(scope);
    this.pendingScopes.delete(storageKey);
    const existing = this.read(storageKey);
    if (existing) this.write(storageKey, { ...existing, pending: false });
  }

  public complete(scope: string): void {
    const storageKey = this.getStorageKey(scope);
    this.pendingScopes.delete(storageKey);
    sessionStorage.removeItem(storageKey);
  }

  public cancel(scope: string): void {
    this.complete(scope);
  }

  public clearAll(): void {
    this.pendingScopes.clear();
    clearStoredPaymentCommands();
  }

  private getStorageKey(scope: string): string {
    return `${STORAGE_PREFIX}${this.getTenantId()}:${scope}`;
  }

  private getTenantId(): string {
    try {
      const company = JSON.parse(
        localStorage.getItem('company') ?? '{}',
      ) as Record<string, unknown>;
      return typeof company['_id'] === 'string' ? company['_id'] : 'anonymous';
    } catch {
      return 'anonymous';
    }
  }

  private read(key: string): StoredPaymentCommand | null {
    try {
      const parsed = JSON.parse(
        sessionStorage.getItem(key) ?? 'null',
      ) as Partial<StoredPaymentCommand> | null;
      return parsed &&
        typeof parsed.commandId === 'string' &&
        typeof parsed.fingerprint === 'string'
        ? {
            commandId: parsed.commandId,
            fingerprint: parsed.fingerprint,
            pending: false,
          }
        : null;
    } catch {
      sessionStorage.removeItem(key);
      return null;
    }
  }

  private write(key: string, command: StoredPaymentCommand): void {
    sessionStorage.setItem(key, JSON.stringify(command));
  }
}
